/**
 * forms-api client (tdd-0059 Phase 6).
 *
 * Submits what a user entered into an agent-elicited form, so the MCP tool that
 * asked for it can read the values back and resume.
 *
 * Uses the shared client rather than bare `fetch` (per CLAUDE.md) so this
 * inherits tracing, timeouts and `credentials: "include"` — the last of which
 * matters here: forms are per-user, and the auth cookie is what proves whose
 * form is being submitted.
 */

import { createClient, createTracingFetch } from "@workspace/network";

const tracedFetch = createTracingFetch({
  onError: (url, method, err, ms) =>
    console.error(`[forms] ✗ ${method} ${url} (${ms}ms): ${err}`),
  infraViz: { source: "ai-assist" },
});

const formsClient = createClient({
  // Generous: a submit can land while forms-api (or drawthings behind it) is
  // still cold-starting, and the whole retry budget has to fit inside this.
  timeout: 45_000,
  // Retries ARE right here, contrary to the original reasoning. Apps behind the
  // gateway are lazy-started, so the first request to a cold one gets
  // `503 service_starting` — a transient state that resolves in seconds, not a
  // failure. Without retries the user's filled-in form is simply lost and they
  // must type it again.
  //
  // Safe to retry because the server is idempotent: the first stored value
  // stands, so a duplicate submit cannot corrupt anything. 503/504/502 are in
  // the client's default retryable set; 4xx is not, so a genuine rejection
  // still fails fast.
  retry: { attempts: 3, delay: 1_000, backoff: true, maxDelay: 8_000 },
  credentials: "include",
  fetch: tracedFetch,
});

/**
 * Where forms-api lives.
 *
 * Hardcoded prod default with an env override, matching the API-client
 * convention — apps shouldn't need a `.env` entry for the normal case.
 */
const FORMS_API_URL =
  process.env.NEXT_PUBLIC_FORMS_API_URL || "https://api.forms.gaz.codes";

export interface FormDefinition {
  id: string;
  sessionId: string;
  toolName: string | null;
  schema: { kind: string; spec: string };
  prefill: Record<string, unknown> | null;
  retain: string[];
  durable?: boolean;
  /** Whether submitted values may still be edited. */
  mutable?: boolean;
  submittedAt?: number;
  /** Set once the form's work was declared done by whoever consumed it. */
  completedAt?: number;
  /**
   * The submitted values, when still available.
   *
   * Absent before submission, and after an ephemeral form completes (its values
   * are tombstoned). Present in between, and indefinitely for a durable form.
   */
  values?: Record<string, unknown>;
}

/**
 * What happened to a submission.
 *
 * `already-submitted` is NOT a failure. Submitting a form twice — a retry, a
 * second click, resubmitting after the agent has read the answers — is refused
 * on purpose so one submission cannot be acted on twice. Collapsing it into
 * "failed" told the user their answers had been lost when they had in fact been
 * saved and used, which is the more alarming of the two by far.
 */
export type SubmitOutcome = "stored" | "already-submitted" | "not-registered" | "failed";

/**
 * Submit a form's collected values.
 *
 * Never throws: the user has already filled the form in, and turning a network
 * blip into a crashed transcript would lose their input entirely.
 */
export async function submitFormValues(
  formId: string,
  values: Record<string, unknown>,
): Promise<SubmitOutcome> {
  try {
    // A duplicate submit is a SUCCESS with `alreadySubmitted: true`, not an
    // error status — the server stores the first value and reports that it did
    // nothing, rather than rejecting. So the signal is in the body.
    const { data } = await formsClient.post<{ alreadySubmitted?: boolean }>(
      `${FORMS_API_URL}/v1/forms/${formId}/response`,
      { values },
    );
    if (data?.alreadySubmitted) {
      console.info(`[forms] ${formId} was already submitted; first value stands`);
      return "already-submitted";
    }
    return "stored";
  } catch (err) {
    // 404 has one cause in practice: the form was rendered without being
    // registered. Distinguished so the model is told to call create_form
    // rather than left to guess — it guessed "session synchronization error"
    // and invented a registry that does not exist.
    if ((err as { status?: number })?.status === 404) {
      console.error(`[forms] ${formId} was never registered via create_form`);
      return "not-registered";
    }
    console.error(`[forms] failed to submit ${formId}:`, err);
    return "failed";
  }
}

/**
 * Fetch a form definition — the reload-survival path.
 *
 * A form rendered from a fenced block is re-derived from the message content,
 * so this is only needed when the transcript is gone but the form id survives
 * (a deep link, or a resumed session).
 */
export async function fetchForm(formId: string): Promise<FormDefinition | null> {
  try {
    const { data } = await formsClient.get<{ form: FormDefinition }>(
      `${FORMS_API_URL}/v1/forms/${formId}`,
    );
    return data.form;
  } catch {
    return null;
  }
}
